import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Heart, DollarSign, Tag, User, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api, useAuth, formatApiErrorDetail } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CATEGORIES, getCategoryLabel } from '../lib/categories';

export default function WantedPage() {
  const { user } = useAuth();
  const [wanted, setWanted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'coins',
    max_budget: '',
    condition_preference: ''
  });

  const fetchWanted = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (activeSearch) params.append('search', activeSearch);
      params.append('page', page);
      params.append('limit', 20);

      const { data } = await api.get(`/wanted?${params.toString()}`);
      setWanted(data.wanted);
      setTotalPages(data.pages);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [category, activeSearch, page]);

  useEffect(() => {
    fetchWanted();
  }, [fetchWanted]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput);
    setPage(1);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to create a wanted listing');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/wanted', {
        ...formData,
        max_budget: formData.max_budget ? parseFloat(formData.max_budget) : null
      });
      toast.success('Wanted listing created!');
      setShowCreateDialog(false);
      setFormData({ title: '', description: '', category: 'coins', max_budget: '', condition_preference: '' });
      fetchWanted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-2">
              Looking For Something?
            </p>
            <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white">
              Wanted Listings
            </h1>
            <p className="text-zinc-400 mt-2">
              Post what you're looking for and let sellers come to you
            </p>
          </div>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <button className="btn-gold px-6 py-3 flex items-center gap-2" data-testid="create-wanted-btn">
                <Plus className="w-5 h-5" /> Post Wanted
              </button>
            </DialogTrigger>
            <DialogContent className="bg-surface-card border-border-subtle max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-clash text-xl text-white">Create Wanted Listing</DialogTitle>
                <DialogDescription className="sr-only">Create a new wanted listing</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">What are you looking for? *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g., 1909-S VDB Lincoln Cent"
                    className="input-dark w-full px-4 py-3"
                    data-testid="wanted-title-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                    placeholder="Describe what you're looking for in detail..."
                    className="input-dark w-full px-4 py-3 h-24 resize-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Category</label>
                    <Select value={formData.category} onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="w-full input-dark border-border-subtle">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface-card border-border-subtle">
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Max Budget</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                      <input
                        type="number"
                        value={formData.max_budget}
                        onChange={(e) => setFormData(f => ({ ...f, max_budget: e.target.value }))}
                        placeholder="Optional"
                        className="input-dark w-full pl-8 pr-4 py-3"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Condition Preference</label>
                  <input
                    type="text"
                    value={formData.condition_preference}
                    onChange={(e) => setFormData(f => ({ ...f, condition_preference: e.target.value }))}
                    placeholder="e.g., MS-65 or better, original packaging"
                    className="input-dark w-full px-4 py-3"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="submit-wanted-btn"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Post Wanted Listing'}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search wanted items..."
              className="input-dark w-full pl-12 pr-4 py-3"
              data-testid="wanted-search-input"
            />
          </form>

          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full sm:w-[200px] input-dark border-border-subtle">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-surface-card border-border-subtle">
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-48 bg-surface-card animate-pulse" />
            ))}
          </div>
        ) : wanted.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {wanted.map(item => (
                <div key={item.id} className="p-6 bg-surface-card border border-border-subtle hover:border-gold/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-gold/10 text-gold border border-gold/20">
                          {getCategoryLabel(item.category)}
                        </span>
                      </div>
                      <h3 className="font-clash text-lg font-medium text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-zinc-400 line-clamp-2 mb-4">
                        {item.description}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                        {item.max_budget && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-gold" />
                            Up to ${item.max_budget?.toLocaleString()}
                          </span>
                        )}
                        {item.condition_preference && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-4 h-4" />
                            {item.condition_preference}
                          </span>
                        )}
                      </div>
                    </div>
                    <Heart className="w-6 h-6 text-gold flex-shrink-0" />
                  </div>

                  <div className="mt-4 pt-4 border-t border-border-subtle flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <User className="w-4 h-4" />
                      <span>{item.user_name}</span>
                    </div>
                    {user && user.id !== item.user_id && (
                      <Link
                        to={`/messages?receiver=${item.user_id}`}
                        className="text-sm text-gold hover:text-gold-hover"
                      >
                        I have this item
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary px-4 py-2 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-zinc-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary px-4 py-2 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-zinc-600 mx-auto mb-6" />
            <h3 className="font-clash text-xl font-medium text-white mb-2">No wanted listings yet</h3>
            <p className="text-zinc-400 mb-6">Be the first to post what you're looking for!</p>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="btn-gold px-6 py-3"
            >
              Post a Wanted Listing
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
